"use client";

import { t } from "@/lib/i18n";
import React, { useEffect, useRef, useState } from "react";
import { useRouter, usePathname } from "next/navigation";
import { useToast } from "./ToastProvider";
import { AlertTriangle, LogOut, CheckCircle2 } from "lucide-react";
import { endAuthSession } from "@/lib/api";

export default function SessionTimeout() {
  const router = useRouter();
  const pathname = usePathname();
  const { showToast } = useToast();
  
  const [showModal, setShowModal] = useState(false);
  const [timeLeft, setTimeLeft] = useState(180); // 3 minutes in seconds
  
  // 12 minutes idle + 3 minutes countdown = 15 minutes total
  const IDLE_TIMEOUT_MS = 12 * 60 * 1000; 
  const COUNTDOWN_DURATION_MS = 3 * 60 * 1000; 
  
  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  const performLogout = async () => {
    const ssoLogoutUrl = await endAuthSession();
    showToast(t("Session ended due to inactivity"), "warning");
    if (ssoLogoutUrl) {
      window.location.assign(ssoLogoutUrl);
      return;
    }
    router.push("/login");
  };

  const updateActivity = () => {
    if (showModal) return; // Force interaction if modal is showing
    localStorage.setItem("lastActivity", Date.now().toString());
  };
  
  const throttledUpdateActivity = () => {
    const last = parseInt(localStorage.getItem("lastActivity") || "0", 10);
    const now = Date.now();
    if (now - last > 2000) { // Throttle writes to localStorage (max once every 2s)
      updateActivity();
    }
  };

  useEffect(() => {
    if (pathname === "/login") return;

    if (!localStorage.getItem("lastActivity")) {
      updateActivity();
    }

    const events = ["pointerdown", "keydown", "scroll", "touchstart"];
    const listenerOptions: AddEventListenerOptions = { passive: true };

    events.forEach((event) => {
      document.addEventListener(event, throttledUpdateActivity, listenerOptions);
    });

    checkIntervalRef.current = setInterval(() => {
      if (showModal) return;
      const lastActivity = parseInt(localStorage.getItem("lastActivity") || "0", 10);
      const now = Date.now();
      
      if (now - lastActivity >= IDLE_TIMEOUT_MS) {
        setShowModal(true);
        if (!localStorage.getItem("logoutAt")) {
          localStorage.setItem("logoutAt", (now + COUNTDOWN_DURATION_MS).toString());
        }
      }
    }, 3000); 

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, throttledUpdateActivity, listenerOptions);
      });
      if (checkIntervalRef.current) clearInterval(checkIntervalRef.current);
    };
  }, [pathname, showModal]);

  useEffect(() => {
    if (!showModal) {
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
      return;
    }

    countdownIntervalRef.current = setInterval(() => {
      const lastActivity = parseInt(localStorage.getItem("lastActivity") || "0", 10);
      
      // If another tab has recent activity, cancel the modal
      if (Date.now() - lastActivity < IDLE_TIMEOUT_MS) {
        localStorage.removeItem("logoutAt");
        setShowModal(false);
        return;
      }

      const logoutAt = parseInt(localStorage.getItem("logoutAt") || "0", 10);
      if (!logoutAt) return;

      const remaining = Math.max(0, Math.ceil((logoutAt - Date.now()) / 1000));
      setTimeLeft(remaining);

      if (remaining <= 0) {
        if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
        localStorage.removeItem("logoutAt");
        setShowModal(false); 
        performLogout();
      }
    }, 1000);

    return () => {
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    };
  }, [showModal]);

  const handleKeepSession = () => {
    localStorage.removeItem("logoutAt");
    localStorage.setItem("lastActivity", Date.now().toString()); // Set langsung untuk bypass if(showModal)
    setShowModal(false);
  };

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  if (!showModal) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 transition-all duration-300">
      <div className="glass-card max-w-md w-full bg-white/95 border border-white shadow-2xl rounded-3xl overflow-hidden p-8 text-center scale-100 transition-all duration-300">
        <div className="w-20 h-20 bg-red-100/80 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner border border-red-200/50">
          <AlertTriangle className="w-10 h-10 text-red-500" />
        </div>
        
        <h2 className="text-2xl font-black text-slate-800 tracking-tight mb-3">
          {t("Your Session Is About to End")}
        </h2>
        
        <p className="text-slate-500 text-sm font-medium mb-8 leading-relaxed">
          {t("Due to inactivity, your session will be closed automatically soon to keep your data secure.")}
        </p>

        <div className="bg-slate-50/80 border border-slate-200/60 rounded-2xl p-5 mb-8 shadow-inner">
          <div className="text-[11px] font-bold text-slate-400 uppercase tracking-[0.2em] mb-2">{t("Automatic Log Out In")}</div>
          <div className="text-5xl font-black text-red-500 font-mono tracking-tight tabular-nums">
            {formatTime(timeLeft)}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={performLogout}
            className="flex-1 py-3.5 px-4 rounded-xl text-sm font-bold text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 hover:text-red-600 transition-all shadow-sm flex items-center justify-center gap-2"
          >
            <LogOut className="w-4 h-4" />
            {t("Log Out")}
          </button>
          <button
            onClick={handleKeepSession}
            className="flex-1 py-3.5 px-4 rounded-xl text-sm font-bold text-white bg-gradient-to-br from-[color:var(--color-primary)] to-blue-600 hover:shadow-lg hover:shadow-blue-500/20 hover:-translate-y-0.5 transition-all shadow-md flex items-center justify-center gap-2"
          >
            <CheckCircle2 className="w-4 h-4" />
            {t("Continue Session")}
          </button>
        </div>
      </div>
    </div>
  );
}
